import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  Loader2,
  LogOut,
  RefreshCw,
  ChevronRight,
  Trash2,
  X,
  Route,
  Lock,
  CheckCircle2,
  Play,
  Clock,
  Pause,
  Eye,
  Plus,
  User,
  Heart,
  ChevronDown,
  Copy,
  FlaskConical,
  RotateCcw,
  CloudSun,
  ChevronLeft,
  Info,
} from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

// Inject scoped style: override .truncate inside simulator to allow wrapping
const simulatorStyle = document.createElement("style");
simulatorStyle.textContent = `.simulator-root .truncate { white-space: break-spaces !important; overflow: visible !important; text-overflow: unset !important; }`;
if (!document.querySelector("[data-simulator-style]")) {
  simulatorStyle.setAttribute("data-simulator-style", "");
  document.head.appendChild(simulatorStyle);
}

// ─── Log types ───────────────────────────────────────────────────────────────
interface LogEntry {
  id: number;
  timestamp: string;
  method: string;
  url: string;
  body?: any;
  status?: number;
  response?: any;
  error?: string;
  durationMs?: number;
}

let logIdCounter = 0;

function createApiCall(addLog: (entry: LogEntry) => void) {
  return async function apiCall(method: string, path: string, token?: string, body?: any) {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const url = `${baseUrl}/api${path}`;
    const logId = ++logIdCounter;
    const start = performance.now();

    try {
      const res = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      const durationMs = Math.round(performance.now() - start);

      addLog({
        id: logId,
        timestamp: new Date().toLocaleTimeString("fr-FR", { hour12: false }),
        method,
        url: `/api${path}`,
        body: body || undefined,
        status: res.status,
        response: data,
        durationMs,
      });

      if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);
      return data;
    } catch (e: any) {
      const durationMs = Math.round(performance.now() - start);
      addLog({
        id: logId,
        timestamp: new Date().toLocaleTimeString("fr-FR", { hour12: false }),
        method,
        url: `/api${path}`,
        body: body || undefined,
        error: e.message,
        durationMs,
      });
      throw e;
    }
  };
}

// ─── Log Panel ───────────────────────────────────────────────────────────────
function LogPanel({ logs, onClear }: { logs: LogEntry[]; onClear: () => void }) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs.length]);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copié dans le presse-papier");
    });
  };

  return (
    <div className="flex flex-col h-[812px] w-full border border-border rounded-xl bg-background overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30 shrink-0">
        <span className="text-sm font-medium">API Logs</span>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {logs.length}
          </Badge>
          <Button variant="ghost" size="sm" onClick={onClear} className="h-7 px-2 text-xs">
            <X className="h-3 w-3 mr-1" /> Effacer
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1 w-full overflow-hidden">
        <div className="p-2 space-y-1 w-full overflow-hidden" style={{ maxWidth: "100%" }}>
          {logs.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-8">Aucun appel API pour le moment</p>
          )}
          {logs.map((log) => {
            const isError = log.error || (log.status && log.status >= 400);
            const expanded = expandedIds.has(log.id);
            return (
              <div
                key={`${log.id}-${log.status ?? "pending"}`}
                className={`text-left rounded-lg border p-2 text-xs transition-colors overflow-hidden ${
                  isError ? "border-destructive/30 bg-destructive/5" : "border-border hover:bg-accent/30"
                }`}
                style={{ overflow: "hidden", maxWidth: "100%", width: "100%", boxSizing: "border-box" }}
              >
                <div className="flex items-center gap-2 cursor-pointer" style={{ minWidth: 0 }} onClick={() => toggle(log.id)}>
                  <Badge
                    variant={log.method === "GET" ? "secondary" : "default"}
                    className="text-[10px] px-1.5 py-0 font-mono shrink-0"
                  >
                    {log.method}
                  </Badge>
                  <span className="font-mono truncate select-text" style={{ minWidth: 0, flex: "1 1 0%" }} title={log.url}>{log.url}</span>
                  {log.status && (
                    <Badge
                      variant={log.status < 400 ? "outline" : "destructive"}
                      className="text-[10px] px-1.5 py-0 shrink-0"
                    >
                      {log.status}
                    </Badge>
                  )}
                  {log.durationMs !== undefined && (
                    <span className="text-muted-foreground shrink-0 whitespace-nowrap">{log.durationMs}ms</span>
                  )}
                </div>
                <div className="text-muted-foreground mt-0.5 cursor-pointer" onClick={() => toggle(log.id)}>{log.timestamp}</div>
                {expanded && (
                   <div className="mt-2 space-y-2 select-text overflow-hidden" style={{ minWidth: 0, maxWidth: "100%" }}>
                    <div className="flex items-start gap-1 overflow-hidden">
                      <Badge variant="outline" className="text-[10px] px-1 py-0 font-mono shrink-0 mt-0.5">{log.method}</Badge>
                      <span className="font-mono text-[10px] select-all break-all" style={{ minWidth: 0, flex: "1 1 0%" }}>{log.url}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 shrink-0"
                        onClick={() => copyToClipboard(`${log.method} ${log.url}`)}
                        title="Copier l'URL"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    {log.body && (
                      <div className="overflow-hidden" style={{ minWidth: 0 }}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">Request:</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1.5 text-[10px] shrink-0"
                            onClick={() => copyToClipboard(JSON.stringify(log.body, null, 2))}
                          >
                            <Copy className="h-3 w-3 mr-0.5" /> Copier
                          </Button>
                        </div>
                        <div className="mt-0.5 rounded bg-muted/50 overflow-hidden">
                          <pre className="p-2 overflow-x-auto overflow-y-auto max-h-40 whitespace-break-spaces select-text text-xs" style={{ margin: 0, maxWidth: "100%" }}>
                            {JSON.stringify(log.body, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                    {log.response && (
                      <div className="overflow-hidden" style={{ minWidth: 0 }}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">Response:</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1.5 text-[10px] shrink-0"
                            onClick={() => copyToClipboard(JSON.stringify(log.response, null, 2))}
                          >
                            <Copy className="h-3 w-3 mr-0.5" /> Copier
                          </Button>
                        </div>
                        <div className="mt-0.5 rounded bg-muted/50 overflow-hidden">
                          <pre className="p-2 overflow-x-auto overflow-y-auto max-h-60 whitespace-break-spaces select-text text-xs" style={{ margin: 0, maxWidth: "100%" }}>
                            {JSON.stringify(log.response, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                    {log.error && !log.response && (
                      <div>
                        <span className="font-semibold text-destructive">Error:</span>
                        <span className="ml-1 select-text">{log.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    </div>
  );
}

type Screen = "auth" | "onboarding" | "paths_list" | "path_activities" | "path_activity";
type BottomTab = "paths" | "habits" | "meteo" | "profile";

interface SimState {
  screen: Screen;
  token: string | null;
  refreshToken: string | null;
  userId: string | null;
  profile: any;
  currentPath: any;
  currentActivities: any[];
  currentActivityIndex: number;
  currentCard: any;
  currentPathActivityId: string | null;
  pathsList: any[];
  readOnly: boolean;
  activeTab: BottomTab;
  testMode: boolean;
}

const initialState: SimState = {
  screen: "auth",
  token: null,
  refreshToken: null,
  userId: null,
  profile: null,
  currentPath: null,
  currentActivities: [],
  currentActivityIndex: 0,
  currentCard: null,
  currentPathActivityId: null,
  pathsList: [],
  readOnly: false,
  activeTab: "paths",
  testMode: false,
};

// ─── Shell with bottom nav ───────────────────────────────────────────────────
function SimulatorShell({
  children,
  onReset,
  activeTab,
  onTabChange,
  showNav,
}: {
  children: React.ReactNode;
  onReset: () => void;
  activeTab: BottomTab;
  onTabChange: (tab: BottomTab) => void;
  showNav: boolean;
}) {
  return (
    <div className="relative shrink-0">
      <div className="w-[375px] h-[812px] rounded-[40px] border-[6px] border-foreground/20 bg-background overflow-hidden shadow-xl flex flex-col">
        <div className="h-12 bg-foreground/5 flex items-center justify-between px-6 text-xs text-muted-foreground shrink-0">
          <span>MindsUp Simulator</span>
          <button
            onClick={onReset}
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            title="Réinitialiser"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
        {showNav && (
          <div className="shrink-0 border-t border-border bg-background flex">
            {[
              { key: "paths" as BottomTab, label: "Parcours", icon: Route },
              { key: "meteo" as BottomTab, label: "Météo", icon: CloudSun },
              { key: "habits" as BottomTab, label: "Habitudes", icon: Heart },
              { key: "profile" as BottomTab, label: "Profil", icon: User },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => onTabChange(key)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] transition-colors ${
                  activeTab === key ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-5 w-5" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Auth Screen ─────────────────────────────────────────────────────────────
const TEST_USER = {
  email: "user@example.com",
  password: "MonMotDePasse123!",
  first_name: "Jean",
  last_name: "Dupont",
  birth_date: "1990-05-15",
};

function AuthScreen({
  onAuth,
  apiCall,
}: {
  onAuth: (token: string, refreshToken: string, userId: string, profile: any) => void;
  apiCall: ReturnType<typeof createApiCall>;
}) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [email, setEmail] = useState(TEST_USER.email);
  const [password, setPassword] = useState(TEST_USER.password);
  const [firstName, setFirstName] = useState(TEST_USER.first_name);
  const [lastName, setLastName] = useState(TEST_USER.last_name);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const data = await apiCall("POST", "/v1/auth/login", undefined, { email, password });
      onAuth(data.session.access_token, data.session.refresh_token, data.user.id, data.profile);
    } catch {
      void 0;
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setLoading(true);
    try {
      const data = await apiCall("POST", "/v1/auth/signup", undefined, {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        birth_date: TEST_USER.birth_date,
      });
      onAuth(data.session.access_token, data.session.refresh_token, data.user.id, data.profile);
    } catch {
      void 0;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTestUser = async () => {
    setDeleting(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-test-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ email: TEST_USER.email }),
      });
      const result = await res.json();
      if (res.status === 404) {
        toast.info("Aucun utilisateur test trouvé");
      } else if (!res.ok) {
        throw new Error(result.error);
      } else {
        toast.success("Utilisateur test supprimé");
      }
    } catch {
      // erreur silencieuse
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 flex flex-col h-full">
      <h1 className="text-xl font-bold text-center mb-6">Bienvenue</h1>
      <Tabs defaultValue="login" className="flex-1">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="login">Connexion</TabsTrigger>
          <TabsTrigger value="signup">Inscription</TabsTrigger>
        </TabsList>
        <TabsContent value="login" className="space-y-4 mt-4">
          <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input
            placeholder="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button className="w-full" onClick={handleLogin} disabled={loading}>
            {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />} Se connecter
          </Button>
        </TabsContent>
        <TabsContent value="signup" className="space-y-4 mt-4">
          <Input placeholder="Prénom" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input placeholder="Nom" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input
            placeholder="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button className="w-full" onClick={handleSignup} disabled={loading}>
            {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />} S'inscrire
          </Button>
        </TabsContent>
      </Tabs>
      <div className="pt-4 border-t border-border mt-4">
        <Button
          variant="outline"
          className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={handleDeleteTestUser}
          disabled={deleting}
        >
          {deleting ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Trash2 className="mr-2 h-4 w-4" />}
          Supprimer l'utilisateur test
        </Button>
      </div>
    </div>
  );
}

// ─── Status helpers ──────────────────────────────────────────────────────────
function statusLabel(status: string) {
  switch (status) {
    case "to_do":
      return "À faire";
    case "hold":
      return "Verrouillé";
    case "in_progress":
      return "En cours";
    case "standby":
      return "En attente";
    case "completed":
      return "Terminé";
    default:
      return status;
  }
}

function statusIcon(status: string) {
  switch (status) {
    case "hold":
      return <Lock className="h-4 w-4 text-muted-foreground" />;
    case "to_do":
      return <Play className="h-4 w-4 text-primary" />;
    case "in_progress":
      return <Clock className="h-4 w-4 text-blue-500" />;
    case "standby":
      return <Pause className="h-4 w-4 text-amber-500" />;
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    default:
      return null;
  }
}

function statusBadgeVariant(status: string): "outline" | "default" | "secondary" | "destructive" {
  if (status === "completed") return "secondary";
  if (status === "in_progress") return "default";
  return "outline";
}

// ─── Habit Form Dialog ───────────────────────────────────────────────────────
const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
const DAY_LABELS = ["L", "Ma", "Me", "J", "V", "S", "D"];

interface HabitFormData {
  name: string;
  difficulty: "easy" | "medium" | "hard";
  target_days: Record<string, boolean>;
  time_of_day: string;
  context: string;
}

const emptyHabitForm: HabitFormData = {
  name: "",
  difficulty: "easy",
  target_days: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  },
  time_of_day: "",
  context: "",
};

function HabitFormDialog({
  open,
  onClose,
  apiCall,
  token,
  editHabit,
  prefillName,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  apiCall: (method: string, path: string, token?: string, body?: any) => Promise<any>;
  token: string;
  editHabit?: any;
  prefillName?: string;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<HabitFormData>(emptyHabitForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      if (editHabit) {
        setForm({
          name: editHabit.name || "",
          difficulty: editHabit.difficulty || "easy",
          target_days: editHabit.target_days || emptyHabitForm.target_days,
          time_of_day: editHabit.time_of_day || "",
          context: editHabit.context || "",
        });
      } else {
        setForm({ ...emptyHabitForm, name: prefillName || "" });
      }
    }
  }, [open, editHabit, prefillName]);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        difficulty: form.difficulty,
        target_days: form.target_days,
        time_of_day: form.time_of_day || undefined,
        context: form.context || undefined,
      };
      if (editHabit) {
        await apiCall("PUT", `/v1/me/habits/${editHabit.id}`, token, body);
      } else {
        await apiCall("POST", "/v1/me/habits", token, body);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editHabit) return;
    setDeleting(true);
    try {
      await apiCall("DELETE", `/v1/me/habits/${editHabit.id}`, token);
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleDay = (day: string) => {
    setForm((f) => ({ ...f, target_days: { ...f.target_days, [day]: !f.target_days[day] } }));
  };

  const difficultyColor = (d: string, selected: boolean) => {
    if (!selected) return "border-input bg-background text-foreground hover:bg-accent/50";
    if (d === "easy") return "border-green-500 bg-green-500 text-white";
    if (d === "medium") return "border-orange-500 bg-orange-500 text-white";
    return "border-red-500 bg-red-500 text-white";
  };

  const selectedDaysCount = Object.values(form.target_days).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[340px] rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editHabit ? "Modifier l'habitude" : "Nouvelle habitude"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Nom</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ex: Méditation, Course, Lecture..."
            />
          </div>

          {/* Days */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Jours de la semaine</Label>
            <div className="flex gap-1.5">
              {DAY_KEYS.map((day, i) => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`w-9 h-9 rounded-full text-xs font-medium border transition-colors ${
                    form.target_days[day]
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-input hover:bg-accent/50"
                  }`}
                >
                  {DAY_LABELS[i]}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {selectedDaysCount === 0
                ? "Sélectionne au moins un jour"
                : `${selectedDaysCount} jour${selectedDaysCount > 1 ? "s" : ""} / semaine`}
            </p>
          </div>

          {/* Difficulty */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Difficulté</Label>
            <div className="flex gap-2">
              {(["easy", "medium", "hard"] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setForm((f) => ({ ...f, difficulty: d }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${difficultyColor(d, form.difficulty === d)}`}
                >
                  {d === "easy" ? "Facile" : d === "medium" ? "Moyen" : "Difficile"}
                </button>
              ))}
            </div>
          </div>

          {/* Time of day */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Moment de la journée</Label>
            <Input
              value={form.time_of_day}
              onChange={(e) => setForm((f) => ({ ...f, time_of_day: e.target.value }))}
              placeholder="Ex: Matin au réveil, Après le déjeuner, 18h..."
            />
          </div>

          {/* Context */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Contexte</Label>
            <Textarea
              value={form.context}
              onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
              placeholder="Ex: Dans ma chambre avec les écouteurs, Pendant ma pause déjeuner..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={handleSave} disabled={saving || !form.name.trim()} className="w-full">
            {saving && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
            {editHabit ? "Enregistrer" : "Ajouter l'habitude"}
          </Button>
          {editHabit && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              {deleting && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
              <Trash2 className="h-4 w-4 mr-1" /> Supprimer l'habitude
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Habits Screen ───────────────────────────────────────────────────────────
function HabitsScreen({
  apiCall,
  token,
}: {
  apiCall: (method: string, path: string, token?: string, body?: any) => Promise<any>;
  token: string;
}) {
  const [habits, setHabits] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<Record<string, string[]> | null>(null);
  const [loadingHabits, setLoadingHabits] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editHabit, setEditHabit] = useState<any>(null);
  const [prefillName, setPrefillName] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const loadHabits = useCallback(async () => {
    setLoadingHabits(true);
    try {
      const data = await apiCall("GET", "/v1/me/habits", token);
      setHabits(data.habits || []);
    } catch {
      void 0;
    } finally {
      setLoadingHabits(false);
    }
  }, [apiCall, token]);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const data = await apiCall("GET", "/v1/habits", token);
      setCatalog(data.habits || null);
    } catch {
      void 0;
    } finally {
      setLoadingCatalog(false);
    }
  }, [apiCall, token]);

  useEffect(() => {
    loadHabits();
    loadCatalog();
  }, [loadHabits, loadCatalog]);

  const activeHabits = habits.filter((h) => h.enabled);
  const pausedHabits = habits.filter((h) => !h.enabled);

  const toggleEnabled = async (habit: any) => {
    try {
      await apiCall("PUT", `/v1/me/habits/${habit.id}`, token, { enabled: !habit.enabled });
      loadHabits();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const openCreate = () => {
    setEditHabit(null);
    setPrefillName("");
    setDialogOpen(true);
  };

  const openEdit = (habit: any) => {
    setEditHabit(habit);
    setPrefillName("");
    setDialogOpen(true);
  };

  const openFromSuggestion = (name: string) => {
    setEditHabit(null);
    setPrefillName(name);
    setDialogOpen(true);
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const daysCount = (h: any) => {
    if (!h.target_days) return 0;
    return Object.values(h.target_days).filter(Boolean).length;
  };

  const diffBadge = (d: string) => {
    if (d === "easy")
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Facile</span>;
    if (d === "medium")
      return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Moyen</span>;
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">Difficile</span>;
  };

  const renderHabitItem = (h: any, showCheckbox = true) => (
    <div
      key={h.id}
      className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-accent/30 transition-colors cursor-pointer"
      onClick={() => openEdit(h)}
    >
      {showCheckbox && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            toggleEnabled(h);
          }}
        >
          <Checkbox checked={h.enabled} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${!h.enabled ? "text-muted-foreground line-through" : ""}`}>
          {h.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-muted-foreground">{daysCount(h)}j/sem</span>
          {diffBadge(h.difficulty)}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold">Mes habitudes</h1>
          {loadingHabits && <Loader2 className="animate-spin h-4 w-4 text-muted-foreground" />}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Active habits */}
        {activeHabits.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Actives ({activeHabits.length})
            </p>
            {activeHabits.map((h) => renderHabitItem(h))}
          </div>
        )}

        {/* Paused habits */}
        {pausedHabits.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              En pause ({pausedHabits.length})
            </p>
            {pausedHabits.map((h) => renderHabitItem(h))}
          </div>
        )}

        {habits.length === 0 && !loadingHabits && (
          <p className="text-sm text-muted-foreground text-center py-4">Aucune habitude pour le moment</p>
        )}

        {/* Create button */}
        <button
          onClick={openCreate}
          className="w-full p-4 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-accent/30 transition-colors flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
          Créer une nouvelle habitude
        </button>

        {/* Suggestions */}
        {catalog && Object.keys(catalog).length > 0 && (
          <div className="space-y-2">
            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">Suggestions</p>
            {loadingCatalog && <Loader2 className="animate-spin h-4 w-4 text-muted-foreground mx-auto" />}
            {Object.entries(catalog).map(([category, names]) => (
              <div key={category} className="border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-accent/30 transition-colors"
                >
                  <span>{category}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {names.length}
                    </Badge>
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${expandedCategories.has(category) ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>
                {expandedCategories.has(category) && (
                  <div className="border-t border-border">
                    {names.map((name) => (
                      <div
                        key={name}
                        className="flex items-center justify-between px-3 py-2 text-sm hover:bg-accent/20"
                      >
                        <span className="truncate flex-1">{name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-primary shrink-0"
                          onClick={() => openFromSuggestion(name)}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Ajouter
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <HabitFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        apiCall={apiCall}
        token={token}
        editHabit={editHabit}
        prefillName={prefillName}
        onSaved={loadHabits}
      />

      {/* Habit Logs Section */}
      <Separator className="my-4" />
      <HabitLogSection apiCall={apiCall} token={token} habits={habits} />
    </div>
  );
}

// ─── Habit Log Section ───────────────────────────────────────────────────────
function HabitLogSection({
  apiCall,
  token,
  habits,
}: {
  apiCall: (method: string, path: string, token?: string, body?: any) => Promise<any>;
  token: string;
  habits: any[];
}) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall("GET", `/v1/me/habit-logs?from=${weekAgo}&to=${today}`, token);
      setLogs(data.logs || []);
    } catch { void 0; }
    finally { setLoading(false); }
  }, [apiCall, token, weekAgo, today]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const toggleLog = async (habitId: string, date: string) => {
    const exists = logs.find((l: any) => l.habit_id === habitId && l.logged_date === date);
    if (exists) {
      await apiCall("DELETE", "/v1/me/habit-logs", token, { habit_id: habitId, date });
    } else {
      await apiCall("POST", "/v1/me/habit-logs", token, { habit_id: habitId, date });
    }
    loadLogs();
  };

  const enabledHabits = habits.filter((h: any) => h.enabled);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Suivi quotidien (7 derniers jours)</h3>
      {loading ? (
        <Loader2 className="animate-spin h-4 w-4 text-muted-foreground mx-auto" />
      ) : enabledHabits.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune habitude active</p>
      ) : (
        <div className="space-y-1">
          {enabledHabits.map((habit: any) => (
            <div key={habit.id} className="flex items-center gap-2">
              <span className="text-xs truncate w-24">{habit.name}</span>
              <div className="flex gap-1">
                {Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(Date.now() - (6 - i) * 86400000).toISOString().split("T")[0];
                  const done = logs.some((l: any) => l.habit_id === habit.id && l.logged_date === d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleLog(habit.id, d)}
                      className={`w-5 h-5 rounded text-[9px] border ${done ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                      title={d}
                    >
                      {new Date(d).getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Meteo Screen ────────────────────────────────────────────────────────────
function MeteoScreen({
  apiCall,
  token,
  onStartMeteoPath,
}: {
  apiCall: (method: string, path: string, token?: string, body?: any) => Promise<any>;
  token: string;
  onStartMeteoPath: (pathId: string, weekStartDate: string) => void;
}) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekStatus, setWeekStatus] = useState<any>(null);
  const [weekLoading, setWeekLoading] = useState(false);

  // Load settings to get week_start_day
  const getWeekStartForOffset = useCallback((offset: number) => {
    // Compute current week start (Monday by default), then subtract offset weeks
    const now = new Date();
    const day = now.getUTCDay();
    const diff = (day - 1 + 7) % 7; // 1 = Monday
    const weekStart = new Date(now);
    weekStart.setUTCDate(now.getUTCDate() - diff - offset * 7);
    return weekStart.toISOString().split("T")[0];
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const hist = await apiCall("GET", "/v1/me/meteo?page=1&per_page=50", token);
      setHistory(hist.meteos || []);
    } catch { void 0; }
    finally { setLoading(false); }
  }, [apiCall, token]);

  const loadWeekStatus = useCallback(async (offset: number) => {
    setWeekLoading(true);
    try {
      const weekStart = getWeekStartForOffset(offset);
      const data = await apiCall("GET", `/v1/me/meteo/current?week_start_date=${weekStart}`, token);
      setWeekStatus(data);
    } catch { setWeekStatus(null); }
    finally { setWeekLoading(false); }
  }, [apiCall, token, getWeekStartForOffset]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadWeekStatus(weekOffset); }, [weekOffset, loadWeekStatus]);

  const goToPreviousWeek = () => setWeekOffset((o) => o + 1);
  const goToNextWeek = () => setWeekOffset((o) => Math.max(0, o - 1));

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border shrink-0 flex items-center justify-between">
        <h1 className="text-lg font-bold">Météo Mentale</h1>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { loadHistory(); loadWeekStatus(weekOffset); }}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Week navigator */}
        <Card>
          <CardHeader className="pb-2 pt-3 px-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={goToPreviousWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-center">
                <p className="text-sm font-semibold">
                  {weekStatus?.week_start_date || "..."} → {weekStatus?.week_end_date || "..."}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {weekOffset === 0 ? "Semaine en cours" : `Il y a ${weekOffset} semaine${weekOffset > 1 ? "s" : ""}`}
                </p>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={goToNextWeek} disabled={weekOffset === 0}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {weekLoading ? (
              <Loader2 className="animate-spin h-4 w-4 text-muted-foreground mx-auto my-2" />
            ) : weekStatus ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant={weekStatus.status === "completed" ? "default" : "outline"} className="text-[10px]">
                    {weekStatus.status === "completed" ? "✅ Réalisée" : weekStatus.status === "in_progress" ? "⏳ En cours" : weekStatus.status === "unavailable" ? "⚠️ Non configurée" : "📋 À faire"}
                  </Badge>
                  {weekStatus.meteo_version && (
                    <Badge variant="secondary" className="text-[10px]">{weekStatus.meteo_version}</Badge>
                  )}
                </div>
                {weekStatus.status !== "completed" && weekStatus.status !== "unavailable" && weekStatus.path_id && (
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => onStartMeteoPath(weekStatus.path_id, weekStatus.week_start_date)}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    {weekStatus.status === "in_progress" ? "Continuer la météo" : "Commencer la météo"}
                  </Button>
                )}
                {weekStatus.status === "unavailable" && weekStatus.reason && (
                  <p className="text-xs text-muted-foreground">{weekStatus.reason}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Impossible de charger</p>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Historique</h3>
          {loading ? (
            <Loader2 className="animate-spin h-4 w-4 text-muted-foreground mx-auto" />
          ) : history.length > 0 ? (
            history.map((m: any) => (
              <div key={m.id} className="text-xs rounded border border-border p-3 space-y-1">
                <p className="font-medium">{m.week_start_date} → {m.week_end_date}</p>
                <p>Bien-être : <span className="font-semibold">{m.wellbeing_score ?? "—"}</span> · Souffrance : <span className="font-semibold">{m.suffering_score ?? "—"}</span></p>
                <p>Habitudes : {m.habits_count}/{m.habits_total}</p>
                {m.note && <p className="text-muted-foreground italic">"{m.note}"</p>}
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground">Aucune météo réalisée</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Profile Screen ──────────────────────────────────────────────────────────
function ProfileScreen({
  apiCall,
  token,
  onLogout,
}: {
  apiCall: (method: string, path: string, token?: string, body?: any) => Promise<any>;
  token: string;
  onLogout: () => void;
}) {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState("");

  useEffect(() => {
    setLoading(true);
    apiCall("GET", "/v1/me/profile", token)
      .then((data) => {
        const p = data.profile;
        setProfile(p);
        setFirstName(p.first_name || "");
        setLastName(p.last_name || "");
        setBirthDate(p.birth_date || "");
      })
      .catch(() => void 0)
      .finally(() => setLoading(false));
  }, [apiCall, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await apiCall("PUT", "/v1/me/profile", token, {
        first_name: firstName,
        last_name: lastName,
        birth_date: birthDate || undefined,
      });
      setProfile(data.profile);
      toast.success("Profil mis à jour");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border shrink-0">
        <h1 className="text-lg font-bold">Mon profil</h1>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
          </div>
        ) : (
          <>
            {profile?.email && (
              <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground">Email</Label>
                <p className="text-sm">{profile.email}</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Prénom</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Votre prénom" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Nom</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Votre nom" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Date de naissance</Label>
              <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
              Enregistrer
            </Button>
          </>
        )}
        <Separator />
        <Button variant="outline" className="w-full" onClick={onLogout}>
          <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
        </Button>
      </div>
    </div>
  );
}

// ─── Card Renderer ───────────────────────────────────────────────────────────
function CardRenderer({
  card,
  onSubmit,
  loading,
  onBack,
  readOnly,
}: {
  card: any;
  onSubmit: (responseData: any) => void;
  loading: boolean;
  onBack?: () => void;
  readOnly?: boolean;
}) {
  const existingResponse = card.response?.response_data;
  const [selectedValue, setSelectedValue] = useState<any>(existingResponse?.value ?? null);
  const [textValue, setTextValue] = useState(typeof existingResponse?.value === "string" ? existingResponse.value : "");
  const [multiValues, setMultiValues] = useState<string[]>(
    Array.isArray(existingResponse?.value) ? existingResponse.value : [],
  );
  const [customValues, setCustomValues] = useState<Record<string, string>>(
    existingResponse?.custom_values ?? {},
  );
  const [actionText, setActionText] = useState(existingResponse?.value?.action ?? "");
  const [plannedDate, setPlannedDate] = useState(existingResponse?.value?.planned_end_date?.slice(0, 10) ?? "");
  const [actualDate, setActualDate] = useState(existingResponse?.value?.actual_end_date?.slice(0, 10) ?? "");
  const [customListItems, setCustomListItems] = useState<string[]>(
    Array.isArray(existingResponse?.value) ? existingResponse.value : [""],
  );
  const [showTooltipSheet, setShowTooltipSheet] = useState(false);
  const [fillBlanks, setFillBlanks] = useState<Record<string, string>>(
    Array.isArray(existingResponse?.value)
      ? Object.fromEntries(existingResponse.value.map((b: any) => [b.blank_id, b.value]))
      : {},
  );
  const [wordLinks, setWordLinks] = useState<Record<string, string>>(
    Array.isArray(existingResponse?.value)
      ? Object.fromEntries(existingResponse.value.map((p: any) => [p[0], p[1]]))
      : {},
  );
  const [orderItems, setOrderItems] = useState<string[]>(
    Array.isArray(existingResponse?.value) ? existingResponse.value : [],
  );
  const [customOrderItems, setCustomOrderItems] = useState<Record<string, string>>(
    existingResponse?.value?.items ?? {},
  );
  const [customOrderIds, setCustomOrderIds] = useState<string[]>(existingResponse?.value?.ordered_ids ?? []);

  const content = card.content || {};
  const code = card.template_code;
  const isInteractive = card.is_interactive;
  const title = content.title || content.question_text || content.instruction || content.label || "";
  const subtitle = content.subtitle || "";
  const description = content.description || content.text || content.body || "";
  const explanation = content.explanation || "";

  // Image positioning
  const imgUrl = content.illustration_url || content.image_url;
  const imgPosition = content.illustration_url_position || content.image_url_position || "middle";
  const isLottie = imgUrl && (imgUrl.endsWith(".lottie") || imgUrl.endsWith(".json"));

  const renderImage = () => {
    if (!imgUrl) return null;
    if (isLottie) {
      return (
        <div className="w-full flex justify-center">
          <DotLottieReact src={imgUrl} loop autoplay style={{ width: "100%", maxHeight: 200 }} />
        </div>
      );
    }
    return <img src={imgUrl} alt="" className="w-full rounded-lg max-h-48 object-contain" />;
  };
  const rawChoices = content.options || [];
  const scorePoints = content.score_points || {};
  const choices = rawChoices.map((c: any) => {
    const id = c.id ?? c.value ?? c.label;
    const pts = id !== undefined && scorePoints[id] !== undefined ? scorePoints[id] : c.points;
    return pts !== undefined ? { ...c, points: pts } : c;
  });

  const handleContinue = () => onSubmit({ viewed: true });

  const handleValidate = () => {
    if (code === "true_false") onSubmit({ value: selectedValue });
    else if (code === "single_choice") {
      const resp: Record<string, any> = { value: selectedValue };
      const relevantCustom: Record<string, string> = {};
      if (selectedValue && customValues[selectedValue]) relevantCustom[selectedValue] = customValues[selectedValue];
      if (Object.keys(relevantCustom).length > 0) resp.custom_values = relevantCustom;
      onSubmit(resp);
    }
    else if (code === "multiple_choice") {
      const resp: Record<string, any> = { value: multiValues };
      const relevantCustom: Record<string, string> = {};
      for (const v of multiValues) {
        if (customValues[v]) relevantCustom[v] = customValues[v];
      }
      if (Object.keys(relevantCustom).length > 0) resp.custom_values = relevantCustom;
      onSubmit(resp);
    }
    else if (code === "likert") onSubmit({ value: selectedValue });
    else if (code === "text_block") onSubmit({ value: textValue });
    else if (code === "action") {
      const val: Record<string, any> = {
        action: actionText,
        planned_end_date: plannedDate ? new Date(plannedDate).toISOString() : undefined,
      };
      if (actualDate) val.actual_end_date = new Date(actualDate).toISOString();
      onSubmit({ value: val });
    } else if (code === "custom_list") onSubmit({ value: customListItems.filter(Boolean) });
    else if (code === "dropdown_list") onSubmit({ value: selectedValue });
    else if (code === "fill_in_blank")
      onSubmit({ value: Object.entries(fillBlanks).map(([blank_id, value]) => ({ blank_id, value })) });
    else if (code === "words_link") onSubmit({ value: Object.entries(wordLinks).map(([l, r]) => [l, r]) });
    else if (code === "order") onSubmit({ value: orderItems });
    else if (code === "custom_order") onSubmit({ value: { items: customOrderItems, ordered_ids: customOrderIds } });
    else onSubmit({ value: selectedValue ?? textValue ?? true });
  };

  const canValidate = () => {
    if (readOnly) return false;
    if (code === "text_block") return textValue.trim().length > 0;
    if (code === "single_choice") {
      if (selectedValue === null) return false;
      const opt = choices.find((c: any) => (c.id ?? c.value) === selectedValue);
      if (opt?.is_custom && (!customValues[selectedValue] || customValues[selectedValue].trim() === "")) return false;
      return true;
    }
    if (code === "multiple_choice") {
      if (multiValues.length === 0) return false;
      for (const v of multiValues) {
        const opt = choices.find((c: any) => (c.id ?? c.value) === v);
        if (opt?.is_custom && (!customValues[v] || customValues[v].trim() === "")) return false;
      }
      return true;
    }
    if (code === "action") return actionText.trim().length > 0 && plannedDate.length > 0;
    if (code === "custom_list") return customListItems.filter(Boolean).length >= (content.min_items || 1);
    if (code === "dropdown_list") return selectedValue !== null && selectedValue !== "";
    if (code === "fill_in_blank") return Object.keys(fillBlanks).length > 0;
    if (code === "words_link") return Object.keys(wordLinks).length > 0;
    if (code === "order") return orderItems.length === (content.items?.length || 0);
    if (code === "custom_order") return customOrderIds.length >= (content.min_items || 1);
    return selectedValue !== null;
  };

  // Initialize order items if needed
  useEffect(() => {
    if (code === "order" && content.items && orderItems.length === 0 && !existingResponse) {
      const shuffled = [...content.items].sort(() => Math.random() - 0.5).map((i: any) => i.id);
      setOrderItems(shuffled);
    }
  }, [code, content.items]);

  const renderContentFields = () => {
    const elements: React.ReactNode[] = [];

    if (content.audio_url) {
      const audioUrl = typeof content.audio_url === "object" ? content.audio_url.url : content.audio_url;
      const duration = typeof content.audio_url === "object" ? content.audio_url.duration_seconds : null;
      elements.push(
        <div key="audio" className="space-y-1">
          <audio controls src={audioUrl} className="w-full" />
          {duration && <p className="text-xs text-muted-foreground">Durée : {Math.round(duration)}s</p>}
        </div>,
      );
    }

    if (content.video_url) {
      elements.push(
        <div key="video" className="space-y-1">
          <video controls src={content.video_url} className="w-full rounded-lg" />
          {content.duration_seconds && (
            <p className="text-xs text-muted-foreground">Durée : {content.duration_seconds}s</p>
          )}
          {content.poster_url && <p className="text-xs text-muted-foreground">Poster : {content.poster_url}</p>}
        </div>,
      );
    }

    // Image is rendered separately based on position — not here anymore

    if (content.transcript && content.transcript.length > 0) {
      elements.push(
        <div key="transcript" className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground">Transcript</p>
          {content.transcript.map((t: string, i: number) => (
            <p key={i} className="text-xs text-muted-foreground italic">
              "{t}"
            </p>
          ))}
        </div>,
      );
    }

    if (content.sources && Array.isArray(content.sources)) {
      elements.push(
        <div key="sources" className="space-y-2">
          <p className="text-xs font-semibold">Sources / Ressources</p>
          {content.sources.map((s: any, i: number) => (
            <div key={i} className="border border-border rounded-lg p-2 space-y-1">
              {s.title && <p className="text-xs font-medium">{s.title}</p>}
              {s.label && <p className="text-xs font-medium">{s.label}</p>}
              {s.url && (
                <a href={s.url} target="_blank" rel="noopener" className="text-xs text-primary underline break-all">
                  {s.url}
                </a>
              )}
              {s.liens &&
                s.liens.map((l: any, j: number) => (
                  <div key={j} className="flex items-center gap-2 text-xs">
                    <a href={l.url} target="_blank" rel="noopener" className="text-primary underline break-all">
                      {l.description || l.url}
                    </a>
                  </div>
                ))}
            </div>
          ))}
        </div>,
      );
    }

    if (content.indicator_value !== undefined) {
      elements.push(
        <div key="indicator" className="text-center">
          <span className="text-3xl font-bold text-primary">
            {content.indicator_value}
            {content.indicator_is_percent ? "%" : ""}
          </span>
        </div>,
      );
    }

    if (content.list_title) {
      elements.push(
        <p key="list-title" className="text-sm font-semibold">
          {content.list_title}
        </p>,
      );
    }
    if (content.items && Array.isArray(content.items) && code === "content") {
      const hasImages = content.items.some((item: any) => typeof item === "object" && item !== null && (item.image || item.image_url || item.illustration_url));
      if (hasImages) {
        elements.push(
          <div key="items" className="space-y-2">
            {content.items.map((item: any, i: number) => {
              if (typeof item !== "object" || item === null) return <p key={i} className="text-sm">{String(item)}</p>;
              const imgSrc = item.image || item.image_url || item.illustration_url;
              return (
                <div key={i} className="border border-border rounded-lg overflow-hidden">
                  <div className="p-3 space-y-0.5">
                    {item.title && <p className="text-sm font-medium">{item.title}</p>}
                    {item.subtitle && <p className="text-xs text-muted-foreground">{item.subtitle}</p>}
                    {(item.value || item.label || item.description) && <p className="text-xs">{item.value ?? item.label ?? item.description}</p>}
                  </div>
                  {imgSrc && <img src={imgSrc} alt="" className="w-full aspect-video object-cover" />}
                </div>
              );
            })}
          </div>,
        );
      } else {
        elements.push(
          <ul key="items" className="list-disc list-inside text-sm space-y-1">
            {content.items.map((item: any, i: number) => (
              <li key={i}>{typeof item === "object" && item !== null ? (item.value ?? item.label ?? JSON.stringify(item)) : String(item)}</li>
            ))}
          </ul>,
        );
      }
    }
    if (content.list_items && Array.isArray(content.list_items)) {
      elements.push(
        <ul key="list-items" className="list-disc list-inside text-sm space-y-1">
          {content.list_items.map((item: any, i: number) => (
            <li key={i}>{typeof item === "object" && item !== null ? (item.value ?? item.label ?? JSON.stringify(item)) : String(item)}</li>
          ))}
        </ul>,
      );
    }
    if (content.items_with_image && Array.isArray(content.items_with_image)) {
      elements.push(
        <div key="items-img" className="space-y-2">
          {content.items_with_image.map((item: any, i: number) => {
            const imgSrc = item.image_url || item.image || item.illustration_url;
            return (
              <div key={i} className="border border-border rounded-lg overflow-hidden">
                <div className="p-3 space-y-0.5">
                  {item.title && <p className="text-sm font-medium">{item.title}</p>}
                  {item.subtitle && <p className="text-xs text-muted-foreground">{item.subtitle}</p>}
                  {item.description && <p className="text-xs">{item.description}</p>}
                </div>
                {imgSrc && <img src={imgSrc} alt="" className="w-full aspect-video object-cover" />}
              </div>
            );
          })}
        </div>,
      );
    }

    if (content.instructions) {
      elements.push(
        <p key="instructions" className="text-sm text-muted-foreground bg-muted/30 p-2 rounded-lg">
          {content.instructions}
        </p>,
      );
    }

    if (code === "exit" && content.exit_type) {
      elements.push(
        <div key="exit" className="bg-muted/30 rounded-lg p-3 text-center">
          <p className="text-sm font-medium">
            {content.exit_type === "to_path_list" ? "↩ Retour à la liste des parcours" : "➡ Vers la prochaine activité"}
          </p>
        </div>,
      );
    }

    if (code === "response" && content.response_type) {
      elements.push(
        <Badge
          key="resp-type"
          variant={content.response_type === "right_answer" ? "default" : "destructive"}
          className="text-xs"
        >
          {content.response_type === "right_answer" ? "✅ Bonne réponse" : "❌ Mauvaise réponse"}
        </Badge>,
      );
    }

    return elements;
  };

  const renderInteractiveFields = () => {
    if (readOnly && existingResponse) {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Eye className="h-3 w-3" /> Réponse enregistrée
          </div>
          <pre className="text-xs bg-muted/50 p-3 rounded-lg overflow-auto max-h-40 whitespace-pre-wrap break-all">
            {JSON.stringify(existingResponse, null, 2)}
          </pre>
          {card.response?.score !== null && card.response?.score !== undefined && (
            <p className="text-xs">
              Score : <strong>{card.response.score}</strong>
            </p>
          )}
          {card.response?.is_correct !== null && card.response?.is_correct !== undefined && (
            <p className="text-xs">
              Correct : <strong>{card.response.is_correct ? "Oui" : "Non"}</strong>
            </p>
          )}
        </div>
      );
    }

    switch (code) {
      case "true_false":
        return (
          <div className="space-y-2">
            {content.question_text && <p className="text-sm font-medium">{content.question_text}</p>}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={selectedValue === true ? "default" : "outline"}
                onClick={() => !readOnly && setSelectedValue(true)}
                disabled={readOnly}
              >
                {choices.find((c: any) => c.id === "vrai")?.label || "Vrai"}
              </Button>
              <Button
                variant={selectedValue === false ? "default" : "outline"}
                onClick={() => !readOnly && setSelectedValue(false)}
                disabled={readOnly}
              >
                {choices.find((c: any) => c.id === "faux")?.label || "Faux"}
              </Button>
            </div>
          </div>
        );

      case "single_choice":
        return (
          <div className="space-y-2">
            <RadioGroup
              value={selectedValue ?? ""}
              onValueChange={(v) => !readOnly && setSelectedValue(v)}
              disabled={readOnly}
            >
              {choices.map((c: any, i: number) => {
                const val = c.id ?? c.value ?? c.label ?? String(i);
                const label = c.label ?? c.value ?? String(i);
                const isCustom = !!c.is_custom;
                const isSelected = selectedValue === val;
                return (
                  <div key={i}>
                    <div
                      className="flex items-center space-x-2 p-3 rounded-lg border border-input hover:bg-accent/50 transition-colors"
                    >
                      <RadioGroupItem value={val} id={`choice-${i}`} />
                      <Label htmlFor={`choice-${i}`} className="flex-1 cursor-pointer">
                        {label}
                        {c.points !== undefined && (
                          <span className="text-muted-foreground text-xs ml-1">({c.points}pt)</span>
                        )}
                      </Label>
                    </div>
                    {isCustom && isSelected && (
                      <div className="ml-8 mt-1">
                        <Input
                          placeholder="Votre réponse…"
                          value={customValues[val] || ""}
                          onChange={(e) => setCustomValues((prev) => ({ ...prev, [val]: e.target.value }))}
                          disabled={readOnly}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        );

      case "multiple_choice":
        return (
          <div className="space-y-2">
            {choices.map((c: any, i: number) => {
              const val = c.id ?? c.value ?? c.label ?? String(i);
              const label = c.label ?? c.value ?? String(i);
              const checked = multiValues.includes(val);
              const isCustom = !!c.is_custom;
              return (
                <div key={i}>
                  <button
                    onClick={() =>
                      !readOnly && setMultiValues((prev) => (checked ? prev.filter((v) => v !== val) : [...prev, val]))
                    }
                    className={`w-full text-left p-3 rounded-lg border transition-colors flex items-center gap-3 ${checked ? "border-primary bg-primary/10" : "border-input hover:bg-accent/50"}`}
                    disabled={readOnly}
                  >
                    <Checkbox checked={checked} disabled={readOnly} />
                    <span className="flex-1">{label}</span>
                    {c.points !== undefined && <span className="text-muted-foreground text-xs">({c.points}pt)</span>}
                  </button>
                  {isCustom && checked && (
                    <div className="ml-8 mt-1">
                      <Input
                        placeholder="Votre réponse…"
                        value={customValues[val] || ""}
                        onChange={(e) => setCustomValues((prev) => ({ ...prev, [val]: e.target.value }))}
                        disabled={readOnly}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case "likert":
        if (choices.length > 0) {
          return (
            <div className="space-y-2">
              {content.question_text && <p className="text-sm font-medium">{content.question_text}</p>}
              <RadioGroup
                value={selectedValue ?? ""}
                onValueChange={(v) => !readOnly && setSelectedValue(v)}
                disabled={readOnly}
              >
                {choices.map((c: any, i: number) => {
                  const val = c.id ?? c.value ?? c.label ?? String(i);
                  const label = c.label ?? c.value ?? String(i);
                  return (
                    <div
                      key={i}
                      className="flex items-center space-x-2 p-3 rounded-lg border border-input hover:bg-accent/50 transition-colors"
                    >
                      <RadioGroupItem value={val} id={`likert-${i}`} />
                      <Label htmlFor={`likert-${i}`} className="flex-1 cursor-pointer">
                        {label}
                        {c.points !== undefined && (
                          <span className="text-muted-foreground text-xs ml-1">({c.points}pt)</span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        }
        return (
          <div className="space-y-2">
            {content.question_text && <p className="text-sm font-medium">{content.question_text}</p>}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{content.min_label}</span>
              <span>{content.max_label}</span>
            </div>
            <div className="flex justify-between gap-1">
              {Array.from({ length: content.scale_size || content.scale || 5 }, (_, i) => i + 1).map((n) => (
                <Button
                  key={n}
                  size="sm"
                  variant={selectedValue === n ? "default" : "outline"}
                  onClick={() => !readOnly && setSelectedValue(n)}
                  className="flex-1"
                  disabled={readOnly}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>
        );

      case "text_block":
        return (
          <div className="space-y-2">
            {content.subtitle && <p className="text-sm text-muted-foreground">{content.subtitle}</p>}
            <Textarea
              placeholder={content.placeholder || "Votre réponse..."}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              rows={4}
              maxLength={content.max_length}
              disabled={readOnly}
            />
            {content.max_length && (
              <p className="text-xs text-muted-foreground text-right">
                {textValue.length}/{content.max_length}
              </p>
            )}
          </div>
        );

      case "action":
        return (
          <div className="space-y-3">
            <Textarea
              placeholder="Décrivez votre action..."
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              rows={3}
              disabled={readOnly}
            />
            <div>
              <Label className="text-xs">Date de fin prévue</Label>
              <Input
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div>
              <Label className="text-xs">Date de fin effective (optionnel)</Label>
              <Input
                type="date"
                value={actualDate}
                onChange={(e) => setActualDate(e.target.value)}
                disabled={readOnly}
              />
            </div>
            {!actualDate && plannedDate && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⏸ Sans date effective, le parcours passera en standby.
              </p>
            )}
            {existingResponse?.value?.planned_end_date &&
              !existingResponse?.value?.actual_end_date &&
              !existingResponse?.value?.interrupted &&
              !readOnly && (
                <Button
                  variant="outline"
                  className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={() => onSubmit({ value: { ...existingResponse.value, interrupted: true } })}
                  disabled={loading}
                >
                  ✋ Arrêter l'action
                </Button>
              )}
          </div>
        );

      case "dropdown_list": {
        const listType = content.list_type ?? "country";
        if (listType === "year") {
          const currentYear = new Date().getFullYear();
          const yearOptions: { value: string; label: string }[] = [];
          for (let year = currentYear; year >= 1926; year--) {
            const value = String(year);
            yearOptions.push({ value, label: value });
          }
          return (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Simulateur : liste d&apos;années (1926 → {currentYear})</p>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedValue ?? ""}
                onChange={(e) => !readOnly && setSelectedValue(e.target.value)}
                disabled={readOnly}
              >
                <option value="">— Sélectionnez une année —</option>
                {yearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        return (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Simulateur : saisie libre (liste déroulante sur mobile)</p>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={selectedValue ?? ""}
              onChange={(e) => !readOnly && setSelectedValue(e.target.value)}
              disabled={readOnly}
            >
              <option value="">— Sélectionnez —</option>
              {choices.map((c: any, i: number) => {
                const val = c.id ?? c.value ?? c.label ?? String(i);
                const label = c.label ?? c.value ?? String(i);
                return <option key={i} value={val}>{label}{c.points !== undefined ? ` (${c.points}pt)` : ""}</option>;
              })}
            </select>
            {choices.length === 0 && (
              <Input
                placeholder="Saisir une valeur…"
                value={textValue}
                onChange={(e) => {
                  setTextValue(e.target.value);
                  setSelectedValue(e.target.value);
                }}
                disabled={readOnly}
              />
            )}
          </div>
        );
      }

      case "custom_list":
        return (
          <div className="space-y-2">
            {customListItems.map((item, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => {
                    const next = [...customListItems];
                    next[i] = e.target.value;
                    setCustomListItems(next);
                  }}
                  placeholder={`Item ${i + 1}`}
                  disabled={readOnly}
                />
                {customListItems.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCustomListItems(customListItems.filter((_, j) => j !== i))}
                    disabled={readOnly}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {(!content.max_items || customListItems.length < content.max_items) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCustomListItems([...customListItems, ""])}
                disabled={readOnly}
              >
                + Ajouter
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              Min: {content.min_items ?? 0}
              {content.max_items ? ` / Max: ${content.max_items}` : ""}
            </p>
          </div>
        );

      case "fill_in_blank":
        return (
          <div className="space-y-3">
            {content.template && <p className="text-sm bg-muted/30 p-3 rounded-lg">{content.template}</p>}
            {content.blanks?.map((blank: any) => (
              <div key={blank.id}>
                <Label className="text-xs">{blank.placeholder || blank.id}</Label>
                <Input
                  value={fillBlanks[blank.id] || ""}
                  onChange={(e) => setFillBlanks({ ...fillBlanks, [blank.id]: e.target.value })}
                  disabled={readOnly}
                />
              </div>
            ))}
          </div>
        );

      case "words_link":
        return (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Associez chaque élément de gauche avec un élément de droite</p>
            {content.left_items?.map((left: any) => (
              <div key={left.id} className="flex items-center gap-2">
                <span className="text-sm flex-1 bg-muted/30 p-2 rounded">{left.label}</span>
                <span className="text-xs">→</span>
                <select
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  value={wordLinks[left.id] || ""}
                  onChange={(e) => setWordLinks({ ...wordLinks, [left.id]: e.target.value })}
                  disabled={readOnly}
                >
                  <option value="">—</option>
                  {content.right_items?.map((right: any) => (
                    <option key={right.id} value={right.id}>
                      {right.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        );

      case "order":
        return (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Réordonnez les éléments (cliquez pour déplacer vers le haut)
            </p>
            {orderItems.map((itemId, idx) => {
              const item = content.items?.find((i: any) => i.id === itemId);
              return (
                <button
                  key={itemId}
                  onClick={() => {
                    if (readOnly || idx === 0) return;
                    const next = [...orderItems];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    setOrderItems(next);
                  }}
                  className="w-full text-left p-3 rounded-lg border border-input hover:bg-accent/50 flex items-center gap-2"
                  disabled={readOnly}
                >
                  <span className="text-xs font-semibold text-muted-foreground w-5">{idx + 1}.</span>
                  <span className="flex-1 text-sm">{item?.label || itemId}</span>
                </button>
              );
            })}
          </div>
        );

      case "custom_order":
        return (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Ajoutez des éléments puis ordonnez-les. Min: {content.min_items || 1}
              {content.max_items ? `, Max: ${content.max_items}` : ""}
            </p>
            {customOrderIds.map((id, idx) => (
              <div key={id} className="flex items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground w-5">{idx + 1}.</span>
                <Input
                  value={customOrderItems[id] || ""}
                  onChange={(e) => setCustomOrderItems({ ...customOrderItems, [id]: e.target.value })}
                  placeholder={`Élément ${idx + 1}`}
                  disabled={readOnly}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={readOnly}
                  onClick={() => {
                    if (idx > 0) {
                      const next = [...customOrderIds];
                      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                      setCustomOrderIds(next);
                    }
                  }}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={readOnly}
                  onClick={() => {
                    const next = customOrderIds.filter((_, i) => i !== idx);
                    const items = { ...customOrderItems };
                    delete items[id];
                    setCustomOrderIds(next);
                    setCustomOrderItems(items);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            {(!content.max_items || customOrderIds.length < content.max_items) && !readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newId = `item_${Date.now()}`;
                  setCustomOrderIds([...customOrderIds, newId]);
                  setCustomOrderItems({ ...customOrderItems, [newId]: "" });
                }}
              >
                + Ajouter
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const isCover = imgPosition === "background_cover";

  return (
    <div className={`flex flex-col h-full relative ${isCover ? "min-h-[300px]" : ""}`}>
      {/* Cover image as background */}
      {isCover && imgUrl && !isLottie && (
        <div className="absolute inset-0 z-0">
          <img src={imgUrl} alt="" className="w-full h-full object-cover rounded-lg opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 to-background/90" />
        </div>
      )}

      {/* Scrollable content */}
      <div className={`relative z-10 flex-1 overflow-y-auto p-4 space-y-4`}>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
        )}

        {/* Top position image: above title */}
        {imgPosition === "top" && renderImage()}

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] font-mono">
              {code}
            </Badge>
            {isInteractive && (
              <Badge variant="secondary" className="text-[10px]">
                Interactif
              </Badge>
            )}
            {card.estimated_duration_minutes && (
              <span className="text-[10px] text-muted-foreground">{card.estimated_duration_minutes} min</span>
            )}
          </div>
          {title && (
            <div className="flex items-center gap-1.5">
              <h2 className="text-base font-semibold">{title}</h2>
              {card.tooltips?.items?.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowTooltipSheet(true)}
                  className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Info className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          {subtitle && <p className="text-sm text-muted-foreground italic">{subtitle}</p>}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>

        {/* Middle position image: after title */}
        {imgPosition === "middle" && renderImage()}

        {renderContentFields()}

        {isInteractive && renderInteractiveFields()}

        {explanation && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">💡 {explanation}</div>
        )}

        {/* Tooltip bottom sheet */}
        {showTooltipSheet && card.tooltips?.items?.length > 0 && (
          <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setShowTooltipSheet(false)}>
            <div className="absolute inset-0 bg-black/40" />
            <div
              className="relative w-full max-w-[375px] bg-card rounded-t-2xl p-4 pb-6 space-y-3 animate-in slide-in-from-bottom duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-card-foreground">{card.tooltips.title || "Informations"}</h3>
                <button onClick={() => setShowTooltipSheet(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-2">
                {(card.tooltips.items as any[]).map((item: any, idx: number) => (
                  <a
                    key={idx}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg border border-border p-3 hover:bg-accent/50 transition-colors"
                  >
                    <p className="text-sm text-card-foreground">{item.description}</p>
                    {item.url && <p className="text-xs text-primary truncate mt-1">{item.url}</p>}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bottom position image: just above button */}
        {imgPosition === "bottom" && renderImage()}
      </div>

      {/* Fixed button at bottom */}
      <div className="relative z-10 p-4 pt-2 border-t border-border bg-card">
        {readOnly ? (
          <Button className="w-full" onClick={handleContinue} disabled={loading}>
            {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
            {card.next_card_id ? "Suivant →" : "← Retour aux activités"}
          </Button>
        ) : isInteractive ? (
          <Button className="w-full" onClick={handleValidate} disabled={loading || !canValidate()}>
            {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
            Valider
          </Button>
        ) : (
          <Button className="w-full" onClick={handleContinue} disabled={loading}>
            {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
            Continuer →
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Paths List Screen ───────────────────────────────────────────────────────
function PathsListScreen({
  paths,
  loading,
  onSelect,
  onRefresh,
  onLogout,
  onInit,
  onOpenTestPaths,
}: {
  paths: any[];
  loading: boolean;
  onSelect: (path: any) => void;
  onRefresh: () => void;
  onLogout: () => void;
  onInit: () => void;
  onOpenTestPaths: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Route className="h-5 w-5" /> Mes parcours
          </h1>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onInit}
              disabled={loading}
              title="Réinitialiser les parcours"
              className="h-8 w-8 p-0"
            >
              <Route className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading} className="h-8 w-8 p-0">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground">
            {paths.length} parcours disponible{paths.length > 1 ? "s" : ""}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenTestPaths}
            className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground"
            title="Tester un parcours spécifique"
          >
            <FlaskConical className="h-3 w-3 mr-1" /> Test
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {paths.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground mb-3">Aucun parcours disponible</p>
            <Button variant="outline" size="sm" onClick={onInit}>
              Initialiser les parcours
            </Button>
          </div>
        )}
        {paths.map((p) => {
          const status = p.progress?.status;
          const disabled = status === "hold";
          return (
            <button
              key={p.id}
              disabled={disabled}
              onClick={() => onSelect(p)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${
                disabled
                  ? "opacity-50 cursor-not-allowed border-border bg-muted/30"
                  : "border-border hover:border-primary/50 hover:bg-accent/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{p.title}</span>
                {!disabled && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </div>
              {status && (
                <div className="flex items-center gap-2 mt-2">
                  {statusIcon(status)}
                  <Badge variant={statusBadgeVariant(status)} className="text-xs">
                    {statusLabel(status)}
                  </Badge>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Test Paths Dialog ───────────────────────────────────────────────────────
function TestPathsDialog({
  open,
  onClose,
  userId,
  onTestPath,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  onTestPath: (pathData: any) => void;
}) {
  const [paths, setPaths] = useState<any[]>([]);
  const [progressMap, setProgressMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadPaths = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: progressData }] = await Promise.all([
        supabase
          .from("path")
          .select("*, theme:theme_id(*)")
          .order("updated_at", { ascending: false }),
        supabase
          .from("user_path_progress")
          .select("path_id, status")
          .eq("user_id", userId),
      ]);

      // Build progress map: path_id -> status
      const pMap = new Map<string, string>();
      for (const pg of progressData || []) {
        pMap.set(pg.path_id, pg.status);
      }
      setProgressMap(pMap);

      const groups = new Map<string, any[]>();
      for (const p of data || []) {
        const rootId = p.path_root_id || p.id;
        if (!groups.has(rootId)) groups.set(rootId, []);
        groups.get(rootId)!.push(p);
      }

      const result: any[] = [];
      for (const [, versions] of groups) {
        versions.sort((a: any, b: any) => b.version - a.version);
        const draft = versions.find((v: any) => !v.is_published);
        const latestPublished = versions.find((v: any) => v.is_published);
        if (draft) result.push({ ...draft, _isDraft: true });
        if (latestPublished && (!draft || latestPublished.id !== draft.id))
          result.push({ ...latestPublished, _isDraft: false });
      }

      setPaths(result);
    } catch {
      toast.error("Erreur lors du chargement des parcours");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) loadPaths();
  }, [open, loadPaths]);

  const callTestAdmin = async (action: string, pathId: string) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-path-admin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ action, user_id: userId, path_id: pathId }),
      },
    );
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Erreur");
    }
    return res.json();
  };

  const handleReset = async (pathId: string) => {
    setActionLoading(pathId + "_reset");
    try {
      await callTestAdmin("reset", pathId);
      setProgressMap((prev) => { const m = new Map(prev); m.delete(pathId); return m; });
      toast.success("Progression réinitialisée");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleInit = async (pathId: string) => {
    setActionLoading(pathId + "_init");
    try {
      await callTestAdmin("init", pathId);
      setProgressMap((prev) => { const m = new Map(prev); m.set(pathId, "to_do"); return m; });
      toast.success("Parcours initialisé");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTest = async (path: any) => {
    setActionLoading(path.id + "_test");
    try {
      // Init the path for the test user
      await callTestAdmin("init", path.id);

      // Load path data from Supabase (works for both published and draft)
      const { data: pathActivities } = await supabase
        .from("path_activity")
        .select("id, activity_id, order_index")
        .eq("path_id", path.id)
        .order("order_index");

      if (!pathActivities || pathActivities.length === 0) {
        toast.error("Aucune activité dans ce parcours");
        return;
      }

      const activityIds = pathActivities.map((pa: any) => pa.activity_id);
      const { data: activities } = await supabase
        .from("activity")
        .select("*")
        .in("id", activityIds);

      // Resolve first_step_card_id for each activity
      const activitiesList: any[] = [];
      for (const pa of pathActivities) {
        const act = activities?.find((a: any) => a.id === pa.activity_id);
        let firstStepCardId: string | null = null;

        if (act?.first_step_id) {
          const { data: step } = await supabase
            .from("activity_step")
            .select("card_id, card_group_id")
            .eq("id", act.first_step_id)
            .single();

          if (step?.card_id) {
            firstStepCardId = step.card_id;
          } else if (step?.card_group_id) {
            const { data: firstItem } = await supabase
              .from("card_group_item")
              .select("card_id")
              .eq("card_group_id", step.card_group_id)
              .order("order_index")
              .limit(1)
              .single();
            firstStepCardId = firstItem?.card_id || null;
          }
        }

        activitiesList.push({
          id: pa.id,
          activity_id: pa.activity_id,
          order_index: pa.order_index,
          title: act?.title || "Sans titre",
          first_step_card_id: firstStepCardId,
          current_step_card_id: firstStepCardId,
          standby_card_id: null,
          estimated_duration_minutes: act?.estimated_duration_minutes,
          progress: { status: activitiesList.length === 0 ? "to_do" : "hold" },
        });
      }

      onTestPath({
        ...path,
        activities: activitiesList,
      });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const [filterText, setFilterText] = useState("");

  const filteredPaths = useMemo(() => {
    if (!filterText.trim()) return paths;
    const lower = filterText.toLowerCase();
    return paths.filter((p) => p.title.toLowerCase().includes(lower));
  }, [paths, filterText]);

  const handleDelete = async (pathId: string) => {
    setActionLoading(pathId + "_delete");
    try {
      await callTestAdmin("delete", pathId);
      setProgressMap((prev) => { const m = new Map(prev); m.delete(pathId); return m; });
      toast.success("Progression supprimée");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" /> Tester un parcours
          </DialogTitle>
        </DialogHeader>
        <div className="px-1 pb-2">
          <Input
            placeholder="Filtrer par titre..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-2 pr-2">
            {loading && (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
              </div>
            )}
            {!loading && filteredPaths.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun parcours</p>
            )}
            {filteredPaths.map((p) => {
              const status = progressMap.get(p.id);
              const hasProgress = !!status;
              return (
                <div key={p.id} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-sm truncate flex-1">{p.title}</span>
                    <Badge
                      variant={p._isDraft ? "outline" : "default"}
                      className="text-[10px] shrink-0"
                    >
                      {p._isDraft ? `Brouillon v${p.version}` : `Publié v${p.version}`}
                    </Badge>
                    <Badge variant="secondary" className="text-[10px] shrink-0">
                      {p.path_type}
                    </Badge>
                    {hasProgress && (
                      <Badge variant="outline" className="text-[10px] shrink-0 border-primary/50 text-primary">
                        {status}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {hasProgress ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={!!actionLoading}
                        onClick={() => handleReset(p.id)}
                      >
                        {actionLoading === p.id + "_reset" ? (
                          <Loader2 className="animate-spin h-3 w-3 mr-1" />
                        ) : (
                          <RotateCcw className="h-3 w-3 mr-1" />
                        )}
                        Réinitialiser
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={!!actionLoading}
                        onClick={() => handleInit(p.id)}
                      >
                        {actionLoading === p.id + "_init" ? (
                          <Loader2 className="animate-spin h-3 w-3 mr-1" />
                        ) : (
                          <Route className="h-3 w-3 mr-1" />
                        )}
                        Initialiser
                      </Button>
                    )}
                    {hasProgress && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-destructive hover:text-destructive"
                        disabled={!!actionLoading}
                        onClick={() => handleDelete(p.id)}
                      >
                        {actionLoading === p.id + "_delete" ? (
                          <Loader2 className="animate-spin h-3 w-3 mr-1" />
                        ) : (
                          <Trash2 className="h-3 w-3 mr-1" />
                        )}
                        Supprimer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      disabled={!!actionLoading || !hasProgress}
                      title={!hasProgress ? "Le parcours doit être initialisé pour être testé" : ""}
                      onClick={() => handleTest(p)}
                    >
                      {actionLoading === p.id + "_test" ? (
                        <Loader2 className="animate-spin h-3 w-3 mr-1" />
                      ) : (
                        <Play className="h-3 w-3 mr-1" />
                      )}
                      Tester
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Activities List Screen ──────────────────────────────────────────────────
function ActivitiesListScreen({
  pathTitle,
  activities,
  loading,
  onSelectActivity,
  onBack,
}: {
  pathTitle: string;
  activities: any[];
  loading: boolean;
  onSelectActivity: (activity: any, index: number) => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground mb-2 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Parcours
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold">{pathTitle}</h1>
          {loading && <Loader2 className="animate-spin h-4 w-4 text-muted-foreground" />}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {activities.length} activité{activities.length > 1 ? "s" : ""}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activities.map((act, idx) => {
          const status = act.progress?.status || "hold";
          const isLocked = status === "hold";
          const isCompleted = status === "completed";
          const isStandby = status === "standby";
          const canStart = status === "to_do" || status === "in_progress" || isStandby;

          return (
            <button
              key={act.id}
              disabled={isLocked}
              onClick={() => onSelectActivity(act, idx)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${
                isLocked
                  ? "opacity-40 cursor-not-allowed border-border bg-muted/20"
                  : isCompleted
                    ? "border-green-200 bg-green-50/50 hover:bg-green-50"
                    : "border-border hover:border-primary/50 hover:bg-accent/50"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-xs font-semibold">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">{act.title}</span>
                    {!isLocked &&
                      (isCompleted ? (
                        <Eye className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      ))}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    {statusIcon(status)}
                    <Badge variant={statusBadgeVariant(status)} className="text-xs">
                      {statusLabel(status)}
                    </Badge>
                    {act.estimated_duration_minutes && (
                      <span className="text-xs text-muted-foreground">{act.estimated_duration_minutes} min</span>
                    )}
                  </div>
                  {isCompleted && <p className="text-xs text-muted-foreground mt-1">👁 Lecture seule</p>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Simulator ──────────────────────────────────────────────────────────
export default function Simulator() {
  const [state, setState] = useState<SimState>(initialState);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [testPathsOpen, setTestPathsOpen] = useState(false);

  const addLog = useCallback((entry: LogEntry) => {
    setLogs((prev) => {
      const idx = prev.findIndex((l) => l.id === entry.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = entry;
        return next;
      }
      return [...prev, entry];
    });
  }, []);

  const rawApiCall = useCallback(createApiCall(addLog), [addLog]);

  const apiCall = useCallback(
    async (method: string, path: string, token?: string, body?: any) => {
      try {
        return await rawApiCall(method, path, token, body);
      } catch (e: any) {
        if (e.message?.includes("401") || e.message?.includes("Non autorisé") || e.message?.includes("Erreur 401")) {
          const currentRefreshToken = stateRef.current.refreshToken;
          if (currentRefreshToken && !path.includes("/auth/")) {
            try {
              const refreshData = await rawApiCall("POST", "/v1/auth/refresh-token", undefined, {
                refresh_token: currentRefreshToken,
              });
              const newToken = refreshData.session.access_token;
              const newRefreshToken = refreshData.session.refresh_token;
              setState((s) => ({ ...s, token: newToken, refreshToken: newRefreshToken }));
              return await rawApiCall(method, path, newToken, body);
            } catch {
              setState(initialState);
              throw new Error("Session expirée, veuillez vous reconnecter");
            }
          }
        }
        throw e;
      }
    },
    [rawApiCall],
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  const updateState = (partial: Partial<SimState>) => setState((s) => ({ ...s, ...partial }));

  const reset = () => setState(initialState);

  const handleAuth = useCallback(
    async (token: string, refreshToken: string, userId: string, profile: any) => {
      updateState({ token, refreshToken, userId, profile });

      if (profile && !profile.onboarding_completed && profile.onboarding_path_id) {
        setLoading(true);
        try {
          const pathData = await apiCall("GET", `/v1/paths/${profile.onboarding_path_id}`, token);
          const activities = pathData.activities || [];
          let actIdx = 0;
          for (let i = 0; i < activities.length; i++) {
            if (!activities[i].progress || activities[i].progress.status !== "completed") {
              actIdx = i;
              break;
            }
          }
          const act = activities[actIdx];
          if (act) {
            const cardId = act.current_step_card_id || act.first_step_card_id;
            if (cardId) {
              const cardData = await apiCall(
                "GET",
                `/v1/activities/${act.activity_id}/cards/${cardId}?path_activity_id=${act.id}`,
                token,
              );
              updateState({
                screen: "onboarding",
                currentPath: pathData,
                currentActivities: activities,
                currentActivityIndex: actIdx,
                currentCard: cardData,
                currentPathActivityId: act.id,
                readOnly: false,
              });
            }
          }
        } catch (e: any) {
          toast.error(e.message);
        } finally {
          setLoading(false);
        }
      } else {
        await loadPathsList(token);
      }
    },
    [apiCall],
  );

  const loadPathsList = async (token: string) => {
    setLoading(true);
    try {
      const paths = await apiCall("GET", "/v1/paths", token);
      updateState({ screen: "paths_list", pathsList: paths, currentPath: null, currentCard: null, readOnly: false });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCardSubmit = async (responseData: any) => {
    if (!state.token || !state.currentCard) return;

    if (state.readOnly) {
      if (state.currentCard.next_card_id) {
        setLoading(true);
        try {
          const activityId = state.currentActivities[state.currentActivityIndex]?.activity_id;
          const cardData = await apiCall(
            "GET",
            `/v1/activities/${activityId}/cards/${state.currentCard.next_card_id}?path_activity_id=${state.currentPathActivityId}`,
            state.token,
          );
          updateState({ currentCard: cardData });
        } catch (e: any) {
          toast.error(e.message);
        } finally {
          setLoading(false);
        }
      } else {
        updateState({ screen: "path_activities", currentCard: null });
      }
      return;
    }

    setLoading(true);
    try {
      const activityId = state.currentCard.path_activity_id
        ? state.currentCard.path_activity_id
        : state.currentActivities[state.currentActivityIndex]?.activity_id;

      const result = await apiCall("PUT", `/v1/activities/${activityId}/cards/${state.currentCard.id}`, state.token, {
        path_activity_id: state.currentPathActivityId,
        response_data: responseData,
        card_group_id: state.currentCard.card_group_id || null,
      });

      if (result.next_card) {
        updateState({ currentCard: result.next_card });
      } else {
        await handleActivityComplete();
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const reloadTestPathActivities = async (): Promise<any[]> => {
    if (!state.currentPath || !state.userId) return [];
    const { data: pathActivities } = await supabase
      .from("path_activity")
      .select("id, activity_id, order_index")
      .eq("path_id", state.currentPath.id)
      .order("order_index");

    const activityIds = (pathActivities || []).map((pa: any) => pa.activity_id);
    const { data: activities } = await supabase
      .from("activity")
      .select("*")
      .in("id", activityIds);

    const paIds = (pathActivities || []).map((pa: any) => pa.id);
    const { data: progressData } = await supabase
      .from("user_path_activity_progress")
      .select("*")
      .eq("user_id", state.userId)
      .in("path_activity_id", paIds);

    const progressMap = new Map<string, any>();
    for (const p of progressData || []) {
      progressMap.set(p.path_activity_id, p);
    }

    const result: any[] = [];
    for (const pa of pathActivities || []) {
      const act = activities?.find((a: any) => a.id === pa.activity_id);
      const progress = progressMap.get(pa.id);
      let firstStepCardId: string | null = null;
      if (act?.first_step_id) {
        const { data: step } = await supabase
          .from("activity_step")
          .select("card_id, card_group_id")
          .eq("id", act.first_step_id)
          .single();
        if (step?.card_id) {
          firstStepCardId = step.card_id;
        } else if (step?.card_group_id) {
          const { data: firstItem } = await supabase
            .from("card_group_item")
            .select("card_id")
            .eq("card_group_id", step.card_group_id)
            .order("order_index")
            .limit(1)
            .single();
          firstStepCardId = firstItem?.card_id || null;
        }
      }
      result.push({
        id: pa.id,
        activity_id: pa.activity_id,
        order_index: pa.order_index,
        title: act?.title || "Sans titre",
        first_step_card_id: firstStepCardId,
        current_step_card_id: firstStepCardId,
        standby_card_id: null,
        estimated_duration_minutes: act?.estimated_duration_minutes,
        progress: progress ? { status: progress.status } : { status: "hold" },
      });
    }
    return result;
  };

  const handleActivityComplete = async () => {
    if (state.screen === "path_activities" || state.screen === "path_activity") {
      if (state.currentPath && state.token) {
        setLoading(true);
        try {
          if (state.testMode) {
            // In test mode, reload from Supabase directly (works for drafts too)
            const activities = await reloadTestPathActivities();
            const allCompleted =
              activities.length > 0 && activities.every((a: any) => a.progress?.status === "completed");
            if (allCompleted) {
              updateState({ screen: "paths_list", testMode: false, currentPath: null, currentCard: null });
              setTestPathsOpen(true);
            } else {
              updateState({
                screen: "path_activities",
                currentActivities: activities,
                currentCard: null,
              });
            }
          } else {
            const pathData = await apiCall("GET", `/v1/paths/${state.currentPath.id}`, state.token);
            const activities = pathData.activities || [];
            const allCompleted =
              activities.length > 0 && activities.every((a: any) => a.progress?.status === "completed");
            if (allCompleted) {
              if (state.activeTab === "meteo") {
                // Return to meteo screen after completing meteo path
                updateState({ screen: "paths_list", currentPath: null, currentCard: null });
              } else {
                await loadPathsList(state.token);
              }
            } else {
              updateState({
                screen: "path_activities",
                currentPath: pathData,
                currentActivities: activities,
                currentCard: null,
              });
            }
          }
        } catch (e: any) {
          toast.error(e.message);
        } finally {
          setLoading(false);
        }
      }
    } else if (state.screen === "onboarding") {
      const nextIdx = state.currentActivityIndex + 1;
      if (nextIdx < state.currentActivities.length) {
        const act = state.currentActivities[nextIdx];
        const cardId = act.first_step_card_id;
        if (cardId && state.token) {
          const cardData = await apiCall(
            "GET",
            `/v1/activities/${act.activity_id}/cards/${cardId}?path_activity_id=${act.id}`,
            state.token,
          );
          updateState({
            currentActivityIndex: nextIdx,
            currentCard: cardData,
            currentPathActivityId: act.id,
          });
        }
      } else {
        try {
          await apiCall("POST", "/v1/paths/init", state.token!);
        } catch {
          // may already be initialized
        }
        await loadPathsList(state.token!);
      }
    }
  };

  const handleCardBack = async () => {
    if (!state.currentCard?.previous_card_id || !state.token) return;
    setLoading(true);
    try {
      const activityId = state.currentActivities[state.currentActivityIndex]?.activity_id;
      const cardData = await apiCall(
        "GET",
        `/v1/activities/${activityId}/cards/${state.currentCard.previous_card_id}?path_activity_id=${state.currentPathActivityId}`,
        state.token,
      );
      updateState({ currentCard: cardData });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPath = async (path: any) => {
    if (!state.token) return;
    setLoading(true);
    try {
      const pathData = await apiCall("GET", `/v1/paths/${path.id}`, state.token);
      updateState({
        screen: "path_activities",
        currentPath: pathData,
        currentActivities: pathData.activities || [],
        currentCard: null,
      });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectActivity = async (activity: any, index: number) => {
    if (!state.token) return;
    const status = activity.progress?.status || "hold";
    const isCompleted = status === "completed";
    const isStandby = status === "standby";

    setLoading(true);
    try {
      const cardId = isCompleted
        ? activity.first_step_card_id
        : isStandby
          ? activity.standby_card_id || activity.current_step_card_id || activity.first_step_card_id
          : activity.current_step_card_id || activity.first_step_card_id;
      if (cardId) {
        const cardData = await apiCall(
          "GET",
          `/v1/activities/${activity.activity_id}/cards/${cardId}?path_activity_id=${activity.id}`,
          state.token,
        );
        updateState({
          screen: "path_activity",
          currentActivityIndex: index,
          currentCard: cardData,
          currentPathActivityId: activity.id,
          readOnly: isCompleted,
        });
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: BottomTab) => {
    if (tab === "paths" && state.token) {
      loadPathsList(state.token);
    }
    updateState({ activeTab: tab, screen: tab === "paths" ? "paths_list" : state.screen, currentCard: null });
  };

  const activityProgressPercent =
    state.currentCard?.max_card_count > 0
      ? Math.round((state.currentCard.current_card_count / state.currentCard.max_card_count) * 100)
      : 0;

  const showBottomNav = state.token && state.screen !== "auth" && state.screen !== "onboarding";
  const isInPathFlow = state.screen === "path_activities" || state.screen === "path_activity";

  return (
    <div className="simulator-root flex gap-6 items-start p-4 min-h-[calc(100vh-4rem)]">
      <SimulatorShell
        onReset={reset}
        activeTab={state.activeTab}
        onTabChange={handleTabChange}
        showNav={!!showBottomNav}
      >
        {state.screen === "auth" && <AuthScreen onAuth={handleAuth} apiCall={apiCall} />}

        {state.screen === "onboarding" && state.currentCard && (
          <div className="flex flex-col h-full">
            <div className="bg-primary/10 text-primary text-xs font-medium text-center py-1.5 shrink-0">
              🎓 Onboarding
            </div>
            <div className="px-4 pt-2 shrink-0">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{state.currentActivities[state.currentActivityIndex]?.title}</span>
                <span>
                  {state.currentCard?.current_card_count ?? 0}/{state.currentCard?.max_card_count ?? "?"}
                </span>
              </div>
              <Progress value={activityProgressPercent} className="h-1.5" />
              <p className="text-xs text-muted-foreground mt-1">{state.currentPath?.title}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              <CardRenderer
                key={state.currentCard.id}
                card={state.currentCard}
                onSubmit={handleCardSubmit}
                loading={loading}
                onBack={state.currentCard.previous_card_id ? handleCardBack : undefined}
              />
            </div>
          </div>
        )}

        {state.screen === "path_activity" && state.currentCard && (state.activeTab === "paths" || state.activeTab === "meteo") && (
          <div className="flex flex-col h-full">
            <div className="px-4 pt-2 shrink-0">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{state.currentActivities[state.currentActivityIndex]?.title}</span>
                <span>
                  {state.currentCard?.current_card_count ?? 0}/{state.currentCard?.max_card_count ?? "?"}
                </span>
              </div>
              <Progress value={activityProgressPercent} className="h-1.5" />
              <p className="text-xs text-muted-foreground mt-1">
                {state.currentPath?.title}
                {state.readOnly && (
                  <Badge variant="secondary" className="ml-2 text-[10px]">
                    Lecture seule
                  </Badge>
                )}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              <CardRenderer
                key={state.currentCard.id}
                card={state.currentCard}
                onSubmit={handleCardSubmit}
                loading={loading}
                readOnly={state.readOnly}
                onBack={
                  state.currentCard.previous_card_id
                    ? handleCardBack
                    : async () => {
                        if (state.currentPath && state.token) {
                          setLoading(true);
                          try {
                            if (state.testMode) {
                              const activities = await reloadTestPathActivities();
                              updateState({
                                screen: "path_activities",
                                currentActivities: activities,
                                currentCard: null,
                              });
                            } else {
                              const pathData = await apiCall("GET", `/v1/paths/${state.currentPath.id}`, state.token);
                              updateState({
                                screen: "path_activities",
                                currentPath: pathData,
                                currentActivities: pathData.activities || [],
                                currentCard: null,
                              });
                            }
                          } catch {
                            updateState({ screen: "path_activities", currentCard: null });
                          } finally {
                            setLoading(false);
                          }
                        } else {
                          updateState({ screen: "path_activities", currentCard: null });
                        }
                      }
                }
              />
            </div>
          </div>
        )}

        {state.screen === "path_activities" && (state.activeTab === "paths" || state.activeTab === "meteo") && (
          <ActivitiesListScreen
            pathTitle={state.currentPath?.title || "Parcours"}
            activities={state.currentActivities}
            loading={loading}
            onSelectActivity={handleSelectActivity}
            onBack={() => {
              if (state.activeTab === "meteo") {
                updateState({ screen: "paths_list", currentPath: null, currentCard: null });
              } else if (state.testMode) {
                updateState({ screen: "paths_list", testMode: false, currentPath: null, currentCard: null });
                setTestPathsOpen(true);
              } else {
                loadPathsList(state.token!);
              }
            }}
          />
        )}

        {state.screen === "paths_list" && state.activeTab === "paths" && (
          <PathsListScreen
            paths={state.pathsList}
            loading={loading}
            onSelect={handleSelectPath}
            onRefresh={() => state.token && loadPathsList(state.token)}
            onLogout={reset}
            onInit={async () => {
              if (!state.token) return;
              setLoading(true);
              try {
                await apiCall("POST", "/v1/paths/init", state.token);
                await loadPathsList(state.token);
              } catch (e: any) {
                toast.error(e.message);
              } finally {
                setLoading(false);
              }
            }}
            onOpenTestPaths={() => setTestPathsOpen(true)}
          />
        )}

        {state.activeTab === "meteo" && state.token && showBottomNav && !isInPathFlow && (
          <MeteoScreen
            apiCall={apiCall}
            token={state.token}
            onStartMeteoPath={async (pathId: string, weekStartDate: string) => {
              if (!state.token) return;
              setLoading(true);
              try {
                // Start meteo (creates user_meteo row with null scores)
                await apiCall("POST", "/v1/me/meteo/start", state.token, { week_start_date: weekStartDate });
                const pathData = await apiCall("GET", `/v1/paths/${pathId}`, state.token);
                updateState({
                  screen: "path_activities",
                  currentPath: pathData,
                  currentActivities: pathData.activities || [],
                  currentCard: null,
                });
              } catch (e: any) {
                toast.error(e.message);
              } finally {
                setLoading(false);
              }
            }}
          />
        )}

        {state.activeTab === "habits" && state.token && showBottomNav && (
          <HabitsScreen apiCall={apiCall} token={state.token} />
        )}

        {state.activeTab === "profile" && state.token && showBottomNav && (
          <ProfileScreen apiCall={apiCall} token={state.token} onLogout={reset} />
        )}

        {loading && state.screen === "auth" && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
            <Loader2 className="animate-spin h-8 w-8 text-primary" />
          </div>
        )}
      </SimulatorShell>

      <div className="flex-1 min-w-0">
        <LogPanel logs={logs} onClear={() => setLogs([])} />
      </div>

      {state.userId && (
        <TestPathsDialog
          open={testPathsOpen}
          onClose={() => setTestPathsOpen(false)}
          userId={state.userId}
          onTestPath={(pathData) => {
            setTestPathsOpen(false);
            updateState({
              screen: "path_activities",
              currentPath: pathData,
              currentActivities: pathData.activities || [],
              currentCard: null,
              testMode: true,
              activeTab: "paths",
            });
          }}
        />
      )}
    </div>
  );
}
